import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu,
    X,
    User,
    ChevronDown,
    LayoutDashboard,
    LogOut,
    Home,
    Info,
    MapPin,
    Package,
    CheckCircle,
    Phone
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import logo from '../images/FINAL-DESIGN-LOGO-01.png';

const Navigation = () => {
    const { user, logout } = useAppContext();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Home', path: '/', icon: Home, type: 'link' },
        { name: 'About', path: '#about', icon: Info, type: 'scroll' },
        { name: 'Services', path: '#services', icon: Package, type: 'scroll' },
        { name: 'Our Impact', path: '#impact', icon: Info, type: 'scroll' },
        { name: 'Why Choose Us', path: '#why-choose', icon: CheckCircle, type: 'scroll' },
        { name: 'Contact', path: '#contact', icon: Phone, type: 'scroll' },
    ];

    const handleNavClick = (link) => {
        setIsMenuOpen(false);
        if (link.type === 'scroll') {
            if (location.pathname !== '/') {
                navigate('/', { state: { scrollTo: link.path } });
            } else {
                const id = link.path.replace('#', '');
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            navigate(link.path);
        }
    };

    return (
        <nav
            className={`fixed w-full top-0 z-50 transition-all duration-500 ${scrolled
                ? 'bg-agro-green-dark border-b border-agro-green-light/30 py-4 shadow-xl'
                : 'bg-transparent py-8'
                }`}
        >
            <div className="container mx-auto px-6">
                <div className="flex items-center justify-between transition-all duration-500">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <img
                            src={logo}
                            alt="SR Agro Farms"
                            className={`${scrolled ? 'h-10' : 'h-14'} w-auto transition-all duration-500 rounded-lg bg-white/10 p-1`}
                        />
                        <div className="flex flex-row items-baseline gap-2 flex-nowrap">
                            <span className={`font-sans font-bold leading-tight whitespace-nowrap ${scrolled ? 'text-white text-lg' : 'text-white md:text-xl'}`}>
                                SR AGRO
                            </span>
                            <span className={`font-sans uppercase tracking-widest text-white font-bold whitespace-nowrap ${scrolled ? 'text-lg' : 'md:text-xl'}`}>
                                FARMS
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-10">
                        {navLinks.map((link) => (
                            <button
                                key={link.name}
                                onClick={() => handleNavClick(link)}
                                className="text-white hover:text-agro-lime transition-all text-sm font-sans font-bold uppercase tracking-widest relative group"
                            >
                                {link.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-agro-lime transition-all duration-300 group-hover:w-full"></span>
                            </button>
                        ))}

                        {/* User Actions */}
                        <div className="flex items-center pl-6 border-l border-white/10">
                            {user ? (
                                <div className="relative">
                                    <button
                                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                                        className="flex items-center gap-3 text-white focus:outline-none py-2 px-4 rounded-full bg-white/10 hover:bg-agro-lime/20 transition-all group"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-agro-lime flex items-center justify-center text-agro-green-dark font-bold text-sm">
                                            {user.name?.charAt(0).toUpperCase() || 'A'}
                                        </div>
                                        <span className="font-medium text-sm hidden lg:block tracking-wide uppercase">{user.name || 'Admin'}</span>
                                        <ChevronDown size={14} className={`transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {isProfileOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 15 }}
                                                className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl overflow-hidden py-1 z-50 border border-gray-100"
                                            >
                                                {user.role === 'admin' && (
                                                    <Link
                                                        to="/admin"
                                                        className="flex items-center gap-3 px-5 py-3 text-sm text-agro-green-dark hover:bg-agro-cream transition-colors font-medium uppercase tracking-wide"
                                                        onClick={() => setIsProfileOpen(false)}
                                                    >
                                                        <LayoutDashboard size={18} />
                                                        Dashboard
                                                    </Link>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        logout();
                                                        setIsProfileOpen(false);
                                                    }}
                                                    className="w-full flex items-center gap-3 px-5 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium uppercase tracking-wide"
                                                >
                                                    <LogOut size={18} />
                                                    Sign Out
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <Link
                                    to="/login"
                                    className="bg-agro-lime text-agro-green-dark py-3 px-8 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-white transition-all transform hover:scale-105"
                                >
                                    Log In
                                </Link>
                            )}
                        </div>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden p-2 text-white bg-white/10 rounded-xl hover:bg-agro-lime transition-all"
                    >
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="md:hidden fixed inset-x-6 top-28 bg-agro-green-dark border border-white/10 p-8 rounded-3xl shadow-2xl z-50 overflow-hidden"
                    >
                        <div className="flex flex-col space-y-6">
                            {navLinks.map((link) => (
                                <button
                                    key={link.name}
                                    onClick={() => handleNavClick(link)}
                                    className="flex items-center gap-4 text-white hover:text-agro-lime py-2 transition-all text-lg font-serif font-bold tracking-wide"
                                >
                                    <link.icon size={20} className="text-agro-lime" />
                                    {link.name}
                                </button>
                            ))}
                            <div className="pt-6 border-t border-white/10 flex flex-col gap-4">
                                {user ? (
                                    <>
                                        {user.role === 'admin' && (
                                            <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="text-white text-lg font-serif tracking-wide flex items-center gap-4">
                                                <LayoutDashboard size={20} className="text-agro-lime" />
                                                Admin Dashboard
                                            </Link>
                                        )}
                                        <button onClick={() => { logout(); setIsMenuOpen(false); }} className="text-red-400 text-lg font-serif tracking-wide flex items-center gap-4">
                                            <LogOut size={20} />
                                            Sign Out
                                        </button>
                                    </>
                                ) : (
                                    <Link to="/login" onClick={() => setIsMenuOpen(false)} className="bg-agro-lime text-agro-green-dark py-4 px-8 rounded-2xl text-center font-bold uppercase tracking-widest">
                                        Sign In
                                    </Link>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navigation;

